import React, { useState, useEffect, useMemo } from "react";
import { Form, Table } from "antd";
import Draggable from "react-draggable";
import { CloseOutlined } from "@ant-design/icons";
import { Modal, Button, Typography, Row, Col } from "antd";
import { BranchSelect } from "../../../components/forms/AutoCompleteFormItem";
import CheckBoxItem from "../../../components/forms/CheckBoxItem";
import { DatePickerItem } from "../../../components/forms/FormItems";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import ChildrenAnimationWrapper from "../../../components/animations/ChildrenAnimationWrapper";
import moment from "moment";
import { listAnnualSequenceLogs } from "../../../api/server";
import { useIntl } from "react-intl";
import { useOrganizationPermission } from "../../../hooks/accessControl";

const { Title } = Typography;

const ExportProspectusesModal = (props) => {
  const {
    onHide,
    visible,
    title,
    btnText,
    initialValues,
    onSubmit,
    branchId,
    loading,
    single,
    onChange,
    isMerhav,
    hideMonth,
  } = props;
  const [disabled, setDisabled] = useState(true);
  const [values, setValues] = useState(true);
  const [form] = Form.useForm();
  const [addSequence, setAddSequence] = useState(false);
  const [sequenceLogs, setSequenceLogs] = useState([]);
  const [selectedSequences, setSelectedSequences] = useState([]);
  const intl = useIntl();
  const [monthValue, setMonthValue] = useState(null);
  const hasOrgAccess = useOrganizationPermission();

  useEffect(() => {
    setValues(initialValues);
    form.setFieldsValue(initialValues);
    if (initialValues && initialValues.month) {
      setMonthValue(initialValues.month);
    }
    return function cleanup() {
      form.resetFields();
    };
  }, [visible]);

  const onCancel = () => {
    onHide();
    form.resetFields();
  };

  const onMouseOver = () => {
    setDisabled(false);
  };

  const onMouseOut = () => {
    setDisabled(true);
  };

  const onValuesChange = (changedValues, allValues) => {
    setValues(allValues);
    if (changedValues["all_branches"]) {
      form.setFieldsValue({ ...allValues, branches: [] });
    }
    if (onChange) {
      onChange(form, changedValues, allValues);
    }
    if (changedValues.hasOwnProperty("add_sequence")) {
      setAddSequence(changedValues.add_sequence);
      setSelectedSequences([]);
      if (!changedValues.add_sequence) {
        // Restore the month value when unchecking add_sequence
        form.setFieldsValue({ month: monthValue });
        form.setFieldsValue({ all_branches: false });
      }
    }
    if (changedValues.hasOwnProperty("month")) {
      setMonthValue(changedValues.month);
    }
  };

  const onFinish = (values) => {
    const payload = {
      ...values,
      month: moment(values.month).format(),
      annual_sequence_numbers: selectedSequences,
    };
    onSubmit(payload);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const layout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
  };

  // isFormValid is now a memoized value, not a function
  const isFormValid = useMemo(() => {
    const hasMonth = hideMonth ? true : !!values.month;
    const hasBranches = values.branches && values.branches.length > 0;
    const hasAllBranches = !!values.all_branches;
    const hasSingleBranch = single && !!values.branches;
    const hasBranchId = !!branchId;
    const hasAddSequence = values.add_sequence;

    return (
      (hasAddSequence && selectedSequences.length > 0) ||
      (!hasAddSequence &&
        hasMonth &&
        (hasBranches || hasAllBranches || hasSingleBranch || hasBranchId))
    );
  }, [values, selectedSequences, hideMonth, single, branchId]);

  const isSubmitDisabled = !isFormValid;

  useEffect(() => {
    if (addSequence) {
      fetchSequenceLogs();
    }
  }, [addSequence, values.year]);

  const fetchSequenceLogs = async () => {
    try {
      const response = await listAnnualSequenceLogs({
        filter: { $and: [{ year: { $eq: moment(values.year).year() } }] },
        limit: 1000,
      });
      setSequenceLogs(response?.data?.items || []); // Ensure it's always an array
    } catch (error) {
      console.error("Error fetching sequence logs:", error);
      setSequenceLogs([]); // Set to empty array on error
    }
  };
  const columns = [
    {
      title: intl.formatMessage({
        id: "app.prospectuses.created_at",
      }),
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => moment(text).format("DD/MM/YYYY"),
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.created_by",
      }),
      dataIndex: ["user", "name"],
      key: "created_by",
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.year",
      }),
      dataIndex: "year",
      key: "year",
    },
    {
      title: intl.formatMessage({
        id: "app.prospectuses.annual_serial_number",
      }),
      dataIndex: "annual_serial_number",
      key: "annual_serial_number",
    },
  ];

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedSequences(selectedRows.map((row) => row.annual_serial_number));
    },
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width="1000px"
      styles={{
        body: {
          padding: 0,
        },
      }}
      open={visible}
      modalRender={(modal) => (
        <Draggable disabled={disabled}>{modal}</Draggable>
      )}
      forceRender
    >
      <div>
        <div
          style={{
            width: "100%",
            cursor: "move",
            height: "50px",
            position: "absolute",
            left: "0",
            top: "0",
          }}
          className="header"
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        >
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px 40px 40px 40px",
          }}
        >
          <Col span={24}>
            <Title
              style={{
                fontSize: "30px",
                fontWeight: 500,
                lineHeight: 1.27,
                color: "rgba(0, 0, 0, 0.85)",
                margin: "13px 0 40px 0 ",
                textAlign: "center",
              }}
            >
              {title}
            </Title>
          </Col>

          <Form
            {...layout}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            onValuesChange={onValuesChange}
            style={{ width: values.add_sequence ? "900px" : "400px" }}
          >
            <Row gutter={20}>
              <Col span={values.add_sequence ? 8 : 24}>
                <ChildrenAnimationWrapper>
                  <Row>
                    <div style={{ flex: 1 }}>
                      {!hideMonth && (
                        <AnimationChildWrapper itemKey={1}>
                          <div
                            style={{
                              display: values.add_sequence ? "none" : "block",
                            }}
                          >
                            <DatePickerItem
                              name="month"
                              category="component"
                              picker="month"
                              format={"MM/YYYY"}
                              required={true}
                            />
                          </div>
                        </AnimationChildWrapper>
                      )}

                      {!branchId &&
                        !values.all_branches &&
                        !values.add_sequence && (
                          <AnimationChildWrapper itemKey={2}>
                            <BranchSelect
                              name="branches"
                              category="component"
                              required={!values.all_branches}
                              mode={single ? "" : "multiple"}
                              disabled={values.all_branches}
                            />
                          </AnimationChildWrapper>
                        )}
                      {!branchId && !single && !values.add_sequence && (
                        <AnimationChildWrapper itemKey={3}>
                          <CheckBoxItem
                            showLabel={true}
                            name="all_branches"
                            category="component"
                          />
                        </AnimationChildWrapper>
                      )}
                      {!values.add_sequence && (
                        <AnimationChildWrapper itemKey={4}>
                          <CheckBoxItem
                            showLabel={true}
                            name="incremental"
                            category="component"
                          />
                        </AnimationChildWrapper>
                      )}

                      <AnimationChildWrapper itemKey={5}>
                        <CheckBoxItem
                          showLabel={true}
                          name="single_file"
                          category="component"
                        />
                      </AnimationChildWrapper>
                      {isMerhav && hasOrgAccess && (
                        <AnimationChildWrapper itemKey={6}>
                          <CheckBoxItem
                            showLabel={true}
                            name="add_sequence"
                            category="component"
                          />
                        </AnimationChildWrapper>
                      )}
                    </div>
                  </Row>
                </ChildrenAnimationWrapper>
              </Col>
              {values.add_sequence && (
                <Col span={16}>
                  <div
                    style={{
                      marginTop: "15px",
                    }}
                  >
                    <DatePickerItem
                      name="year"
                      category="component"
                      picker="year"
                      format={"YYYY"}
                      required={true}
                      style={{ display: "flex", flex: "none" }}
                    />
                    <Table
                      rowSelection={rowSelection}
                      columns={columns}
                      dataSource={sequenceLogs}
                      rowKey="id"
                      size="small"
                      scroll={{ y: 300 }}
                    />
                  </div>
                </Col>
              )}
            </Row>
            <div
              style={{
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                marginTop: "20px",
              }}
            >
              <Button
                htmlType="submit"
                type="primary"
                disabled={isSubmitDisabled}
                loading={loading}
              >
                {btnText}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default ExportProspectusesModal;
